import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { GetPersonalFavoriteListResponse } from "api/generatedApi";
import { sortCompareString } from "utils/compares";
import { TextIcon } from "components/shared/text-icon/text-icon";
import { useOrder } from "shared/table/use-order";
import { TableHeaderConfig } from "shared/table/table-header-type";
import { TableHeader } from "shared/table/table-header";
import { DesktopItemRow } from "./desktop-item-row";
import { useDeleteFavoriteItems } from "../../hooks/use-confirm-delete-item-dialog";
import { Fab } from "@mui/material";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { useCreateItem } from "../../hooks/use-create-item-dialog";

interface Props {
  favoriteList: GetPersonalFavoriteListResponse | undefined;
}

export const DesktopItemList = (props: Props) => {
  type FavoritesSortableId = "Text";

  const { favoriteList } = props;
  const { t } = useTranslation();
  const isOwner = favoriteList?.isOwnedByCurrentUser ?? false;
  const openCreateFavoriteItem = useCreateItem({ ListId: favoriteList?.id });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set<string>());
  const [checkedAll, setCheckedAll] = useState(false);
  const { direction, orderBy, getLabelProps } = useOrder<FavoritesSortableId>("Text");
  const openDeleteFavoritesDialog = useDeleteFavoriteItems({ itemIds: selectedIds, listId: favoriteList?.id, isOwner });

  const onCheck = useCallback(
    (id: string | undefined) => {
      if (id === undefined) return;
      let ids = new Set(selectedIds);
      if (ids.has(id)) {
        ids.delete(id);
      } else {
        ids.add(id);
      }
      setSelectedIds(ids);
    },
    [selectedIds]
  );

  const onCheckAll = (checked: boolean) => {
    let ids = new Set<string>();
    if (checked) {
      favoriteList?.favorites?.forEach((item) => ids.add(item.id ?? ""));
    }
    setSelectedIds(ids);
    setCheckedAll(!checkedAll);
  };

  const isFavoriteChecked = (id: string | undefined): boolean => {
    return selectedIds.has(id ?? "");
  };

  const sortedData = useMemo(() => {
    let sortedList = favoriteList?.favorites;

    switch (orderBy) {
      case "Text":
        if (favoriteList?.favorites) {
          sortedList = [...favoriteList.favorites].sort((a, b) => sortCompareString(direction, a?.text, b?.text));
        }
        break;
    }

    return sortedList;
  }, [favoriteList, orderBy, direction]);

  const isFavoriteDisabled = (): boolean => {
    if (selectedIds.size > 0) {
      if (favoriteList?.isOwnedByCurrentUser) {
        return false;
      }
    }
    return true;
  };

  const headerConfig: TableHeaderConfig<FavoritesSortableId>[] = [
    {
      id: "Text",
      title: t("account.favorites.tableName"),
      sortable: true,
      testid: "account-favorites-header-name",
      alignment: "left",
    },
  ];

  return (
    <Box sx={{ flex: 1, overflowY: "hidden", display: "flex", flexDirection: "column", width: "100%", height: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "right", flexGrow: 1, pb: 1 }}>
        <TextIcon translateText="common.delete">
          <IconButton data-testid="account-favorites-delete-rows" disabled={isFavoriteDisabled()} onClick={openDeleteFavoritesDialog}>
            <DeleteOutlinedIcon />
          </IconButton>
        </TextIcon>
      </Box>
      <TableContainer sx={{ height: "calc(100vh - 240px)", overflowY: "auto", pb: "50px" }} component={Paper}>
        <Table stickyHeader>
          <TableHeader method="checkbox" headerConfig={headerConfig} getLabelProps={getLabelProps} checked={selectedIds.size === favoriteList?.favorites?.length} checkBoxDisabled={false} onCheckAllProps={onCheckAll} />
          <TableBody>
            {sortedData &&
              sortedData.map((favorite) => {
                return <DesktopItemRow key={favorite.id} row={favorite} onCheck={onCheck} checked={isFavoriteChecked(favorite.id)} />;
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {isOwner && (
        <Fab sx={{ position: "absolute", bottom: "30px", right: "10px", backgroundColor: "primary.dark" }} onClick={openCreateFavoriteItem}>
          <TimerOutlinedIcon fontSize="large" sx={{ color: "grey.100", pb: "2px" }} />
        </Fab>
      )}
    </Box>
  );
};
